import { defineComponent, type PropType } from "vue";
import type { ExpertVideoDetailPageProps } from "@newgenerated/shared/schema";
import "./expert-video-detail.scss";
import { GaBadge } from "@/components/general/GaBadge";
import { useGaContext } from "@utils/vue-migration/common/gaContext/gaContext";
import { GaButton } from "@/components/general/GaButton";
import type { GaVueComponent } from "@/common/vueUtils";
import type { TranslationFn } from "@utils/vue-migration/common/gaContext/gaContextTranslations";
import { createStore, type Store } from "@/common/storeUtils";
import { DateTime } from "luxon";
import { GaMarkdownContent, useSafeMarkdownRenderer } from "@/components/general/GaMarkdownContent";
import { useShareModal } from "@/components/general/GaShareModal";
import { ContentCard, getInitialSuspendTranslationFns } from "@/components/cards/ContentCard";
import { assert } from "@utils/assertion";

export const ExpertVideoDetailPage = defineComponent({
  props: {
    detailPageProps: {
      type: Object as PropType<ExpertVideoDetailPageProps>,
      required: true,
    },
  },
  setup: (props) => {
    const { t } = useGaContext();
    const detailPageProps = props.detailPageProps;
    const showMore = createStore<boolean>(false);
    const openModal = createStore<boolean>(false);
    return () => {
      return (
        <>
          <div class={"my-4"}>
            <VideoCard detailPageProps={detailPageProps} openModal={openModal} t={t} />
          </div>
          <div class={"mb-4"}>
            <Description detailPageProps={detailPageProps} showMore={showMore} t={t} />
          </div>
        </>
      );
    };
  },
});

function VideoCard(props: { detailPageProps: ExpertVideoDetailPageProps; openModal: Store<boolean>; t: TranslationFn }): GaVueComponent {
  const translationFnStore = createStore(getInitialSuspendTranslationFns());
  const metadata = { ...props.detailPageProps.expertVideo.metadata, common: { ...props.detailPageProps.expertVideo.metadata.common, link: null } };
  const onClickModalEvent = useShareModal({
    slots: <ContentCard content={metadata} layout={"vertical"} translationFn={translationFnStore.sub(props.detailPageProps.expertVideo.metadata.common.language)} />,
    link: location.href,
    contentItemType: "EXPERT_VIDEO",
    contentId: props.detailPageProps.expertVideo.expertVideoId,
  });

  return (
    <div class={"webinar-detail__video-container"}>
      <div class={"video_iframe"}>
        <iframe
          allow={"fullscreen;"}
          title="video player"
          class="video__iframe_inner"
          width="100%"
          height="100%"
          src={
            props.detailPageProps.expertVideo.vimeoLink !== ""
              ? `https://player.vimeo.com/video/${props.detailPageProps.expertVideo.vimeoLink}`
              : `https://www.youtube-nocookie.com/embed/${props.detailPageProps.expertVideo.youtubeLink}?enablejsapi=1&feature=oembed`
          }></iframe>
      </div>
      <div class={"p-2"}>
        <GaBadge caption={true} variant="secondary-light">
          {props.t("expertVideos:source.expertVideos")}
        </GaBadge>
        <h2 class={"mt-2 mb-1"}>{props.detailPageProps.expertVideo.metadata.common.title}</h2>
        <p>with {props.detailPageProps.expertVideo.metadata.common.leadIn}</p>
        <GaButton onClick={() => onClickModalEvent()} variant={"primary-alt"} type={"button"}>
          <div>
            <i class="ico-share-alt2" />
            {props.t("general:share.short")}
          </div>
        </GaButton>
      </div>
    </div>
  );
}

function Description(props: { detailPageProps: ExpertVideoDetailPageProps; showMore: Store<boolean>; t: TranslationFn }): GaVueComponent {
  const show = props.showMore.get();
  const text = props.detailPageProps.expertVideo.metadata.common.description;
  assert(text !== null);
  const eventDateTime = props.detailPageProps.expertVideo.eventDateTime;
  const dt = DateTime.fromISO(eventDateTime.datetime, { zone: eventDateTime.timezone });
  const formatted = dt.toFormat("MMMM d, yyyy");
  return (
    <div class={"webinar-detail__description-container"}>
      <p class={"webinar-detail__description-title"}>{formatted}</p>
      <div class={!show ? "webinar-detail__description--clamped" : ""}>
        <div class={"webinar-detail__description-md"}>
          <GaMarkdownContent
            input={text}
            defaultRenderer={useSafeMarkdownRenderer(() => (
              <></>
            ))}
          />
        </div>
        <div>
          <h4>{props.t("expertVideos:detail.description.aboutTheAuthor")}</h4>
          <p>{props.detailPageProps.expertVideo.aboutTheHost}</p>
        </div>
      </div>
      <GaButton variant={"link-slim"} type={"button"} onClick={() => props.showMore.set(!show)}>
        <strong>{show ? props.t("general:button.showLess") : (props.t("general:button.showMore") ?? "test")}</strong>
      </GaButton>
    </div>
  );
}
